

import axios from "axios";
import { useContext, useEffect, useState, useCallback, useMemo, useLayoutEffect, useRef } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { FaEdit, FaFilePdf, FaWhatsapp } from "react-icons/fa";
import { ImCross } from "react-icons/im";
import { IoArrowUndoSharp } from "react-icons/io5";
import { RiDeleteBin6Fill } from "react-icons/ri";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { CommonContext } from "../../../App";
import LoadingSpinner from "../../../Components/CommonComponent/loadingSpiner/LoadingSpinner";
import WhatsAppShareModal from "../../../Components/WhatsAppShareModal";
import i18n from "../../../i18n";
import api from "../../../services/api";
import TransactionEditModal from "../../AllTablesPage/TransactionEditModal";
import DeleteDataModal from "../../../Components/CommonComponent/Modals/DeleteDataModal";

export default function LedgerDetails() {
  const API_BASE = process.env.REACT_APP_BASE_URL;
  const SHOW_DOCUMENT = process.env.REACT_APP_SHOW_DOCUMENT_URL;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    ledger,
    addbtn,
    btnHeading,
    btnlink,
    salesRegister,
    paymentRegister,
    receiptRegister,
    purchaseRegister,
    comeBack,
    entry,
  } = location.state || {};

  const { userData, branchId, dateRange } = useContext(CommonContext);
  const adminId = userData?.user?.id;
  const userRole = userData?.user?.role;

  const [startDate, setStartDate] = useState(dateRange.startDate);
  const [endDate, setEndDate] = useState(dateRange.endDate);

  const [balanceData, setBalanceData] = useState([]);
  const [apiResponse, setApiResponse] = useState(null);
  const [loadingTable, setLoadingTable] = useState(false);

  const [openDocument, setOpenDocument] = useState(false);
  const [showDocument, setShowDocument] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [updatedUserData, setUpdatedUserData] = useState([]);

  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfFilename, setPdfFilename] = useState("");
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const [defaultDateRange, setDefaultDateRange] = useState({
    startDate: null,
    endDate: null,
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDeleteEntry, setSelectedDeleteEntry] = useState(null);

  const [sortOrder, setSortOrder] = useState("asc");

  const storedLang = JSON.parse(localStorage.getItem("second_language"));
  const language = storedLang?.language || "en";
  const [focusedRowIndex, setFocusedRowIndex] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const containerRef = useRef(null);
  // ---------------------- HELPERS ----------------------
  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    if (isNaN(d)) return "-";
    return `${String(d.getDate()).padStart(2, "0")}/${String(
      d.getMonth() + 1
    ).padStart(2, "0")}/${d.getFullYear()}`;
  };

  const formatBalance = (amount) => {
    return Math.abs(Number(amount || 0)).toLocaleString("en-IN");
  };

  const getLedgerId = useMemo(() => {
    return (
      ledger?.id ||
      salesRegister?.ledger_id ||
      paymentRegister?.ledger_id ||
      receiptRegister?.ledger_id ||
      purchaseRegister?.ledger_id ||
      entry?.ledger_id
    );
  }, [
    ledger?.id,
    salesRegister?.ledger_id,
    paymentRegister?.ledger_id,
    receiptRegister?.ledger_id,
    purchaseRegister?.ledger_id,
    entry?.ledger_id,
  ]);

  // ---------------------- SYNC GLOBAL DATE RANGE ----------------------
  useEffect(() => {
    setStartDate(dateRange.startDate);
    setEndDate(dateRange.endDate);
  }, [dateRange]);
  useEffect(() => {
    containerRef.current?.focus();
  }, []);
  useEffect(() => {
    setFocusedRowIndex(null);
    setSelectedRows(new Set());
  }, [balanceData]);

  const handleTableKeyDown = (e) => {
    if (!balanceData.length) return;

    // ⬇️ DOWN
    if (e.key === "ArrowDown") {
      e.preventDefault();

      setFocusedRowIndex((prev) => {
        if (prev === null) return 0;
        if (prev >= balanceData.length - 1) return prev;
        return prev + 1;
      });
    }

    // ⬆️ UP
    if (e.key === "ArrowUp") {
      e.preventDefault();

      setFocusedRowIndex((prev) => {
        if (prev === null) return null;
        if (prev <= 0) return 0;
        return prev - 1;
      });
    }

    // ␣ SPACE → SELECT / UNSELECT
    if (e.key === " ") {
      e.preventDefault();

      if (focusedRowIndex === null) return;

      setSelectedRows((prev) => {
        const newSet = new Set(prev);

        if (newSet.has(focusedRowIndex)) {
          newSet.delete(focusedRowIndex);
        } else {
          newSet.add(focusedRowIndex);
        }

        return newSet;
      });
    }
  };
  useLayoutEffect(() => {
    if (focusedRowIndex === null) return;

    const container = containerRef.current;
    const row = document.getElementById(`row-${focusedRowIndex}`);

    if (!container || !row) return;

    const containerHeight = container.clientHeight;
    const scrollTop = container.scrollTop;

    const rowTop = row.offsetTop;
    const rowHeight = row.offsetHeight;

    const rowCenter = rowTop + rowHeight / 2;
    const containerCenter = scrollTop + containerHeight / 2;

    const OFFSET = 60;

    if (rowCenter > containerCenter + OFFSET) {
      container.scrollTo({
        top: rowTop - containerHeight / 2 + rowHeight,
        behavior: "smooth",
      });
    } else if (rowCenter < containerCenter - OFFSET) {
      container.scrollTo({
        top: rowTop - containerHeight / 2,
        behavior: "smooth",
      });
    }
  }, [focusedRowIndex]);
  // ---------------------- FETCH SINGLE LEDGER (HEADER INFO) ----------------------
  const fetchOneledgerById = useCallback(async () => {
    if (!entry || !branchId) return;
    try {
      const res = await api.get(
        `ledgers/get-ledger/${entry?.ledger_id}/${branchId}`
      );
      if (res.data.success) {
        setUpdatedUserData(res.data.data || []);
      }
    } catch (err) {
      toast.error(err.message || "Failed to fetch ledger");
    }
  }, [entry, branchId]);

  useEffect(() => {
    if (branchId) fetchOneledgerById();
  }, [fetchOneledgerById, branchId]);

  // ---------------------- FETCH LEDGER BALANCE DATA ----------------------
  const fetchLedgerData = useCallback(async () => {
    if (!startDate || !endDate || !branchId || !getLedgerId) return;

    try {
      setLoadingTable(true);

      const res = await api.get(
        `ledgers/get-all-balance-ledger/${getLedgerId}`,
        {
          params: { startDate, endDate, branchId, sort: sortOrder },
        }
      );

      if (res.data.success) {
        setBalanceData(res.data.allData || []);
        setApiResponse(res.data || {});
      }
    } catch (err) {
      toast.error("Error fetching ledger data");
    } finally {
      setLoadingTable(false);
    }
  }, [startDate, endDate, branchId, sortOrder, getLedgerId]);

  useEffect(() => {
    fetchLedgerData();
  }, [fetchLedgerData]);

  // ---------------------- ESC KEY CLOSE ----------------------
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape")navigate("/account-books/ledgers-table", { state: {  ledger, againAddbtn: addbtn,
                    btnHeading,
                    btnlink,
                    entry: updatedUserData,
                    date: endDate, } });
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  // ---------------------- DOCUMENT VIEWER DATA ----------------------
  useEffect(() => {
    let docs = [];

    const pushDocs = (docArray, type) => {
      if (docArray) {
        const list = Array.isArray(docArray) ? docArray : JSON.parse(docArray);
        docs = docs.concat(list.map((d) => ({ url: d, type })));
      }
    };

    pushDocs(ledger?.document, "Personal Documents");
    pushDocs(ledger?.bankDocument, "Bank Documents");
    pushDocs(ledger?.taxDocument, "Tax Documents");

    setShowDocument(docs);
    setCurrentIndex(0);
  }, [ledger]);

  // ---------------------- EDIT ENTRY ----------------------
  const handleEditLedger = useCallback(
    (entry) => {
      const entryWithKey = {
        ...entry,
        payment_key:
          entry.voucher_type === "payment" ? entry.payment_key : undefined,
        receipt_key:
          entry.voucher_type === "receipt" ? entry.receipt_key : undefined,
        purchase_key:
          entry.voucher_type === "purchase" ||
            (entry.voucher_type === "transportation" &&
              entry.part === "purchase") ||
            (entry.voucher_type === "loading/unloading" &&
              entry.part === "purchase")
            ? entry.purchase_key
            : undefined,
        entry_key:
          entry.voucher_type === "sales" ||
            (entry.voucher_type === "transportation" && entry.part === "sales") ||
            (entry.voucher_type === "loading/unloading" && entry.part === "sales")
            ? entry.entry_key
            : undefined,
        journal_key:
          entry.voucher_type === "from journal" ||
            entry.voucher_type === "to journal"
            ? entry.entry_key
            : undefined,
        branchId,
      };

      setSelectedEntry(entryWithKey);
      setIsEditModalOpen(true);
    },
    [branchId]
  );

  const handleModalClose = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedEntry(null);
  }, []);

  const handleUpdateSuccess = useCallback(async () => {
    await fetchLedgerData();
    handleModalClose();
  }, [fetchLedgerData, handleModalClose]);

  // ---------------------- DOCUMENT SLIDER ----------------------
  const nextSlide = () =>
    setCurrentIndex((i) => (i === showDocument.length - 1 ? 0 : i + 1));
  const prevSlide = () =>
    setCurrentIndex((i) => (i === 0 ? showDocument.length - 1 : i - 1));

  // ---------------------- PDF DOWNLOAD ----------------------
  const handleDownloadPDF = async () => {
    try {
      const id = getLedgerId;
      if (!id) return toast.error("Ledger ID not found");

      const currentLang = i18n.language || "en";
      const lang = currentLang || "en";

      const url = `${API_BASE}ledgers/export-pdf/${id}?branchId=${branchId}&startDate=${startDate}&endDate=${endDate}&lang=${lang}`;

      const link = document.createElement("a");
      link.href = url;
      link.download = `Ledger_${NAME}_${startDate}_to_${endDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("PDF download started");
    } catch (error) {
      console.error(error);
      toast.error("Error downloading PDF");
    }
  };

  // ---------------------- WHATSAPP SHARE ----------------------
  const handleShareViaWhatsApp = async () => {
    try {
      const id = getLedgerId;
      if (!id) return toast.error("Ledger ID not found");

      setIsGeneratingPDF(true);

      const currentLang = i18n.language || "en";
      const lang = currentLang || "en";

      const response = await api.post(
        `ledgers/share-pdf/${id}?branchId=${branchId}&startDate=${startDate}&endDate=${endDate}&lang=${lang}`
      );

      if (response.data.success) {
        setPdfUrl(response.data.pdfUrl);
        setPdfFilename(response.data.filename);
        setIsWhatsAppModalOpen(true);
        toast.success("PDF generated successfully!");
      } else {
        toast.error(response.data.message || "Failed to generate PDF");
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Error generating PDF for sharing"
      );
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // ---------------------- DELETE ENTRY ----------------------
  const handleDeleteApi = async (entry) => {
    try {
      const voucherType = entry.voucher_type;
      if (!voucherType) throw new Error("Voucher type missing");

      let url = "";
      let payload = {
        branchId,
        adminId,
        role: userRole,
        deleteReason: entry.deleteReason,
        deletePassword: entry.deletePassword,
        entry,
      };

      switch (voucherType) {
        case "sales":
          url = "sales/delete-sales-entry";
          payload.entryKey = entry.entry_key || entry.entryKey;
          break;

        case "purchase":
          url = "purchase/delete-purchase-entry";
          payload.purchaseKey = entry.purchase_key || entry.purchaseKey;
          break;

        case "receipt":
          url = "receipt/delete-receipt-entry";
          payload.receiptKey = entry.receipt_key || entry.receiptKey;
          break;

        case "payment":
          url = "payment/delete-payment-entry";
          payload.paymentKey = entry.payment_key || entry.paymentKey;
          break;

        case "from journal":
        case "to journal":
          url = "journal/delete-journal-entry";
          payload.journalKey =
            entry.journal_key || entry.entry_key || entry.entryKey;
          break;

        default:
          throw new Error("Invalid voucher type: " + voucherType);
      }

      const res = await api.post(url, payload);

      if (res.data.success) {
        toast.success("Entry deleted successfully");
        setShowDeleteModal(false);
        setSelectedDeleteEntry(null);
        fetchLedgerData();
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(
        error.response?.data?.message || error.message || "Delete failed"
      );
    }
  };

  // ---------------------- HEADER DISPLAY DATA ----------------------
  const ID = getLedgerId || "1";

  const NAME =
    ledger?.name ||
    salesRegister?.name ||
    paymentRegister?.name ||
    receiptRegister?.name ||
    purchaseRegister?.name ||
    ledger?.companyName ||
    salesRegister?.customer_name ||
    purchaseRegister?.vendor ||
    ledger?.customerName ||
    ledger?.vendor ||
    ledger?.vehicleName ||
    ledger?.driverName ||
    updatedUserData?.customerName ||
    updatedUserData?.vendor ||
    updatedUserData?.vehicleName ||
    updatedUserData?.driverName ||
    updatedUserData?.name ||
    entry?.name ||
    "";

  const PHONE =
    ledger?.phone ||
    salesRegister?.phone ||
    paymentRegister?.phone ||
    receiptRegister?.phone ||
    purchaseRegister?.phone ||
    updatedUserData?.phone ||
    "";

  const COUSIN =
    ledger?.cousinName ||
    salesRegister?.cousinName ||
    paymentRegister?.cousinName ||
    receiptRegister?.cousinName ||
    purchaseRegister?.cousinName ||
    updatedUserData?.cousinName ||
    "";

  const ADDRESS =
    ledger?.address ||
    salesRegister?.address ||
    paymentRegister?.address ||
    receiptRegister?.address ||
    purchaseRegister?.address ||
    updatedUserData?.address ||
    "";

  // ---------------------- BALANCE CALCULATIONS (FROM API RESPONSE) ----------------------

  // Opening Balance Display Logic
  const calculateOpeningDisplay = () => {
    if (!apiResponse) return { balance: 0, type: "Dr" };

    const previousReceived = Number(apiResponse.previousReceived || 0);
    const previousPaid = Number(apiResponse.previousPaid || 0);
    const openingRunningBalance = Number(apiResponse.openingRunningBalance || 0);
    const hasOpeningBalance = apiResponse.hasOpeningBalance;
    const isOpeningBeforeStartDate = apiResponse.isOpeningBeforeStartDate;
    const isOpeningInDateRange = apiResponse.isOpeningInDateRange;
    const originalOpeningBalance = Number(apiResponse.openingBalance || 0);
    const originalOpeningType = apiResponse.openingBalanceType;

    // Case 1: Previous transactions exist OR opening balance is before startDate
    if (previousReceived > 0 || previousPaid > 0 || isOpeningBeforeStartDate) {
      return {
        balance: Math.abs(openingRunningBalance),
        type: openingRunningBalance >= 0 ? "Dr" : "Cr",
      };
    }

    // Case 2: Opening balance is within date range and no previous transactions
    if (isOpeningInDateRange && hasOpeningBalance) {
      return {
        balance: originalOpeningBalance,
        type: originalOpeningType === "received" ? "Dr" : "Cr",
      };
    }

    // Case 3: No previous data, no opening balance in range
    return {
      balance: 0,
      type: "Dr",
    };
  };

  const openingDisplay = calculateOpeningDisplay();
  const openingBalance = openingDisplay.balance;
  const openingType = openingDisplay.type;

  // ✅ FIXED: Current Period Totals (only current period transactions)
  const currentPeriodReceived = Number(apiResponse?.currentTotalReceived || 0);
  const currentPeriodPaid = Number(apiResponse?.currentTotalPaid || 0);

  // ✅ FIXED: Calculate Closing Balance correctly
  // Closing Balance = Opening Balance + Current Period Received - Current Period Paid
  const calculateClosingBalance = () => {
    if (!apiResponse) return { balance: 0, type: "Dr" };

    // Get opening running balance (includes previous + opening balance if before startDate)
    const openingRunningBalance = Number(apiResponse.openingRunningBalance || 0);

    // Add opening balance if it's within date range
    let effectiveOpening = openingRunningBalance;
    if (apiResponse.isOpeningInDateRange && apiResponse.hasOpeningBalance) {
      const obReceived = Number(apiResponse.openingBalance || 0);
      const obType = apiResponse.openingBalanceType;
      if (obType === "received") {
        effectiveOpening += obReceived;
      } else if (obType === "paid") {
        effectiveOpening -= obReceived;
      }
    }

    // Closing = Opening + Current Received - Current Paid
    const closingBal = effectiveOpening + currentPeriodReceived - currentPeriodPaid;

    return {
      balance: Math.abs(closingBal),
      type: closingBal >= 0 ? "Dr" : "Cr",
    };
  };

  const closingDisplay = calculateClosingBalance();
  const closingBalance = closingDisplay.balance;
  const closingType = closingDisplay.type;

  // ✅ FIXED: Total Received and Paid for Footer
  // Total = Opening Balance (as received/paid) + Current Period
  const calculateTotals = () => {
    let totalReceived = 0;
    let totalPaid = 0;

    // Add opening balance based on type
    if (openingType === "Dr") {
      totalReceived += openingBalance;
    } else {
      totalPaid += openingBalance;
    }

    // Add current period
    totalReceived += currentPeriodReceived;
    totalPaid += currentPeriodPaid;

    return { totalReceived, totalPaid };
  };

  const totals = calculateTotals();
  const totalReceived = totals.totalReceived;
  const totalPaid = totals.totalPaid;

  const handleModalDateChange = (range) => {
    setDefaultDateRange({
      startDate: range.startDate,
      endDate: range.endDate,
    });
  };
  const hasCurrentTransactions = balanceData && balanceData.length > 0;
  return (
    <>
      <div className="container-fluid p-0">
        <div className="pt-2">
          {/* Header */}
          <div className="flex justify-between gap-3">
            <div className="flex justify-between gap-3">
              <div className="mt-[2px]">
                <NavLink
                  to={comeBack ? comeBack : "/account-books/ledgers-table"}
                  state={{
                    ledger,
                    againAddbtn: addbtn,
                    btnHeading,
                    btnlink,
                    entry: updatedUserData,
                    date: endDate,
                  }}
                  className="inline-block p-1 px-2 text-theme-backBtn border-1 border-theme-backBtn rounded cursor-pointer hover:bg-theme-backBtn hover:text-theme-color"
                >
                  <IoArrowUndoSharp />
                </NavLink>
              </div>

              <div>
                {addbtn && (
                  <NavLink
                    to={btnlink}
                    className="inline-block no-underline py-[2px] px-2 font-medium text-theme-backBtn border-1 border-theme-backBtn rounded cursor-pointer hover:bg-theme-backBtn hover:text-theme-color"
                  >
                    {btnHeading}
                  </NavLink>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 mb-2">
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-1 px-3 py-[2px] text-white bg-theme-headerBg rounded-1"
                title="Download PDF"
              >
                <FaFilePdf /> PDF
              </button>

              <button
                onClick={handleShareViaWhatsApp}
                disabled={isGeneratingPDF}
                className="flex items-center gap-1 px-3 py-[2px] text-white bg-theme-headerBg rounded-1 disabled:bg-gray-400 disabled:cursor-not-allowed"
                title="Share via WhatsApp"
              >
                <FaWhatsapp /> {isGeneratingPDF ? "Generating..." : "WhatsApp"}
              </button>
            </div>
          </div>

          {/* Main content */}
          <div className="p-xl-6 bg-theme-sidebarTabBg text-sm shadow-sm mx-auto h-[80vh] border">
            <div className="bg-white text-theme-headerBg">
              <div className="mb-2 pt-2 font-bold text-center">
                {t("accountDetails")}
              </div>

              <div className="grid grid-cols-3 px-4 mb-1 xl:gap-1 md:gap-2 text-sm">
                <div>
                  <strong className="font-medium text-sm">
                    {t("sheetNo")}:
                  </strong>{" "}
                  {ID}
                </div>
                <div>
                  <strong className="font-medium text-sm">
                    {t("accountNo")}:
                  </strong>{" "}
                  {ID}
                </div>
                <div>
                  <strong className="font-medium text-sm">
                    {t("viewDocument")}:
                  </strong>{" "}
                  <button
                    className="p-0 px-1 hover:text-theme-headerBg hover:bg-theme-tableBg border-1 border-theme-headerBg rounded-1"
                    onClick={() => setOpenDocument(true)}
                  >
                    {t("view")}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-3 px-4 mb-1 xl:gap-1 md:gap-2">
                <div>
                  <strong className="font-medium text-sm">
                    {t("accountName")} :
                  </strong>{" "}
                  <span className="p-1 px-2 capitalize text-white font-medium bg-theme-headerBg rounded-1">
                    {NAME} {language !== "en" && COUSIN && `( ${COUSIN} )`}
                  </span>
                </div>
                <div>
                  <strong className="font-medium text-sm">{t("phone")}:</strong>{" "}
                  {PHONE}
                </div>
                <div>
                  <strong className="font-medium text-sm capitalize">
                    {t("address")}:
                  </strong>{" "}
                  {ADDRESS}
                </div>
              </div>

              <div className="flex justify-between gap-3 mt-3">
                <div className="flex gap-2">
                  <div className="inline-block px-2 border-t border-e py-[2px] fw-medium">
                    {t("openingQuantity")}:{" "}
                    {apiResponse?.previousQuantityTypeWise?.length
                      ? apiResponse.previousQuantityTypeWise
                        .map(
                          (i) =>
                            `${i.type_name}: ${formatBalance(i.quantity)}`
                        )
                        .join(" , ")
                      : "0"}
                  </div>
                </div>
                <div className="inline-block px-2 border-t border-s py-[2px] fw-medium">
                  {t("openingBalance")}: {formatBalance(openingBalance)}{" "}
                  {openingType}
                </div>
              </div>
            </div>

            {/* TABLE AREA */}
            <div
              ref={containerRef}
              id="table-container"
              tabIndex={0}
              onKeyDown={handleTableKeyDown}
              className="h-[64vh] overflow-auto p-0 m-0 relative outline-none"
            >
              {loadingTable && (
                <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60">
                  <LoadingSpinner size={60} color="#000000" />
                </div>
              )}

              <table className="w-full text-sm table-fixed">
                <thead className="sticky top-0 py-1 mt-0 bg-theme-headerBg text-theme-color">
                  <tr>
                    <th
                      className="py-[2px] px-1 font-medium text-start w-[90px] cursor-pointer"
                      onClick={() =>
                        setSortOrder((prev) =>
                          prev === "asc" ? "desc" : "asc"
                        )
                      }
                    >
                      {t("date")} {sortOrder === "asc" ? "↓↓" : "↑↑"}
                    </th>
                    <th className="w-1/5 px-1 font-medium">
                      {t("description")}
                    </th>
                    <th className="w-1/5 px-1 font-medium">{t("entries")}</th>
                    <th className=" px-1 font-medium">{t("voucherType")}</th>
                    <th className="px-1 font-medium">{t("voucherNo")}</th>
                    <th className="px-1 font-medium text-end">
                      {t("receivedDr")}
                    </th>
                    <th className="px-1 font-medium text-end">{t("paidCr")}</th>
                    <th className="px-1 font-medium text-end">
                      {t("balance")}
                    </th>
                    <th className="px-1 font-medium text-center">
                      {t("Action")}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {hasCurrentTransactions ? (
                    <>
                      {balanceData.map((entry, index) => {
                        const debit = Number(entry.received || 0);
                        const credit = Number(entry.paid || 0);
                        const runningBal = Number(entry.running_balance || 0);

                        return (
                          <tr
                            id={`row-${index}`}
                            key={index}
                            className={`
                            cursor-pointer
                            ${focusedRowIndex === index
                                ? "bg-[#5c5b5b] text-white "       //  FOCUS (priority high)
                                : selectedRows.has(index)
                                  ? "bg-[#f4c48a]"     //  SELECTED (fallback)
                                  : ""
                              }
  `}
                          >
                            <td className="px-1 border-b">
                              {formatDate(entry.entry_date)}
                            </td>
                            <td className="px-1 py-[2px] border-b">
                              {entry.description}
                            </td>
                            <td className="px-1 py-[2px] border-b capitalize">
                              {entry.entry_info}
                            </td>
                            <td className="px-1 border-b">
                              {entry.voucher_type}
                            </td>
                            <td className="px-1 border-b text-center">
                              {entry.id}
                            </td>
                            <td className="px-1 border-b text-end">
                              {debit ? debit.toLocaleString("en-IN") : ""}
                            </td>
                            <td className="px-1 py-[2px] border-b text-end">
                              {credit ? credit.toLocaleString("en-IN") : ""}
                            </td>
                            <td className="px-1 border-b text-end">
                              {formatBalance(runningBal)}{" "}
                              {runningBal >= 0 ? "Dr" : "Cr"}
                            </td>
                            <td className="px-1 text-center border-b">
                              <div className="flex justify-center gap-2">
                                <FaEdit
                                  className={ `   ${focusedRowIndex === index
                                ? "bg-[#5c5b5b] text-white "       
                                : selectedRows.has(index)
                                  ? "bg-[#f4c48a] text-gray-700"     
                                  : "text-gray-700"
                              }text-gray-700 cursor-pointer hover:text-theme-btnBg`}
                                  onClick={() => handleEditLedger(entry)}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })}

                      {/* ✅ FIXED: Footer Totals with Correct Closing Balance */}
                      <tr className="sticky bottom-0 border-t border-theme-sidebarBg font-semibold bg-white">
                        <td colSpan="5" className="px-1 py-1 text-start ps-3">
                          {t("total")}:
                        </td>
                        <td className="px-1 py-1 text-end">
                          {totalReceived.toLocaleString("en-IN")}
                        </td>
                        <td className="px-1 py-1 text-end">
                          {totalPaid.toLocaleString("en-IN")}
                        </td>
                        <td className="px-1 py-1 text-end">
                          {formatBalance(closingBalance)} {closingType}
                        </td>
                        <td className="px-1 py-1"></td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td colSpan="9" className="px-1 py-1 text-center border">
                        {t("noDataAvailable")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {hasCurrentTransactions && (
              <div className="flex justify-between bg-white border-theme-headerBg border-t">
                <div className="inline-block px-1 pt-[1px] fw-semibold">
                  {t("closingQuantity")}:{" "}
                  {/* {(() => {
        const prevQ = apiResponse?.previousQuantityTypeWise || [];
        const nowQ = apiResponse?.currentQuantityTypeWise || [];
        const sum = {};

        prevQ.forEach((i) => (sum[i.type_name] = Number(i.quantity)));
        nowQ.forEach((i) => {
          sum[i.type_name] =
            (sum[i.type_name] || 0) + Number(i.quantity);
        });

        return (
          Object.entries(sum)
            .filter(([_, qty]) => qty !== 0)
            .map(
              ([type, qty]) =>
                `${type}: ${formatBalance(Math.abs(qty))}`
            )
            .join(" , ") || "0"
        );
      })()} */}
                  {apiResponse?.totalQuantityTypeWise?.length
                    ? apiResponse.totalQuantityTypeWise
                      .filter((i) => Number(i.quantity) !== 0)
                      .map(
                        (i) =>
                          `${i.type_name}: ${formatBalance(Math.abs(i.quantity))}`
                      )
                      .join(" , ")
                    : "0"}
                </div>

                <div className="inline-block px-1 pe-3 pt-[1px] pb-[2px] fw-semibold">
                  {t("closingBalance")}: {formatBalance(closingBalance)}{" "}
                  {closingType}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ---------------------- DOCUMENT VIEWER ---------------------- */}
      {openDocument && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/30 z-[1000]">
          <div className="inline-block border max-w-3xl min-h-[30vh] h-[60vh] w-full p-3 rounded bg-white">
            <div className="flex justify-end">
              <ImCross
                className="cursor-pointer"
                onClick={() => setOpenDocument(false)}
              />
            </div>

            {showDocument.length ? (
              <div className="w-full py-4 px-3 flex flex-col items-center h-[500px]">
                <h3 className="mb-2 text-lg font-semibold">
                  {showDocument[currentIndex].type}
                </h3>

                <div className="h-[40vh]">
                  <img
                    src={`${SHOW_DOCUMENT}${showDocument[currentIndex].url}`}
                    className="object-contain h-full"
                    alt="view Document"
                  />
                </div>

                {showDocument.length > 1 && (
                  <div className="flex justify-between w-full gap-3 mt-3">
                    <button
                      onClick={prevSlide}
                      className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400"
                    >
                      {t("prev")}
                    </button>
                    <span>
                      {currentIndex + 1} / {showDocument.length}
                    </span>
                    <button
                      onClick={nextSlide}
                      className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400"
                    >
                      {t("next")}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="h-full py-5 text-center">
                {t("noDataAvailable")}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------------------- WHATSAPP SHARE MODAL ---------------------- */}
      <WhatsAppShareModal
        isOpen={isWhatsAppModalOpen}
        onClose={() => setIsWhatsAppModalOpen(false)}
        pdfUrl={pdfUrl}
        filename={pdfFilename}
        phone={PHONE}
        accountName={NAME}
        startDate={startDate}
        endDate={endDate}
      />

      {/* ---------------------- EDIT MODAL ---------------------- */}
      <TransactionEditModal
        isOpen={isEditModalOpen}
        onClose={handleModalClose}
        entry={selectedEntry}
        onSuccess={handleUpdateSuccess}
        startDate={endDate}
        endDate={endDate}
        setDefaultDateRange={setDefaultDateRange}
        onDateChange={handleModalDateChange}
      />

      {/* ---------------------- DELETE MODAL ---------------------- */}
      {showDeleteModal && (
        <DeleteDataModal
          selectedLedger={selectedDeleteEntry}
          handleDeleteModalClose={() => {
            setShowDeleteModal(false);
            setSelectedDeleteEntry(null);
          }}
          handleDeleteLedgerApi={handleDeleteApi}
          showinputFields={true}
        />
      )}
    </>
  );
}